import React from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import MusicXML from 'Models/MusicXML';
import Phrase from 'Models/Phrase';
import TimeSignature from 'Models/TimeSignature';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import { TimingOffsetsConfig } from 'Models/EventStream';
import type { AppDispatch } from 'Reducers';
import type { PrerenderedPhraseData } from 'Types';
import { NotifyPhaserEvents, PhaserToReactListeners, RemoveListener } from 'Phaser/EventBus2';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { MODE_CONVERSION_OPTIONS, type ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import { Game } from './MainAppPhaser';
import musicXmlRaw from 'Phaser/__fixtures__/phrases/58343/phrase.musicxml?raw';
import cursorData from 'Phaser/__fixtures__/phrases/58343/cursor_data_1440.json';
import cursorDataTimesig from 'Phaser/__fixtures__/phrases/58343/cursor_data_timesig_1440.json';

const TRANSPOSE_OPTIONS = Array.from({ length: 15 }, (_, index) => index - 7);

const buildPhrase = (sequenceId: number) => {
  const musicXml = new MusicXML(musicXmlRaw, [new TimeSignature(3, 4)]);

  return new Phrase(
    `visual-transposition-${sequenceId}`,
    musicXml,
    13,
    '',
    '',
    cursorData.serializedIterator as PrerenderedPhraseData[],
    cursorDataTimesig.serializedIterator as PrerenderedPhraseData[],
    cursorData.measureRects,
  );
};

/** Development-only route that exercises the real two-phrase Phaser queue. */
const SightReadingTranspositionVisualFixture = () => {
  const dispatch = useDispatch<AppDispatch>();
  const parentRef = React.useRef<HTMLElement | undefined>(undefined);
  const [dimensions, setDimensions] = React.useState<{ width: number; height: number }>();
  const [transposeSemitones, setTransposeSemitones] = React.useState(0);
  const [modeConversion, setModeConversion] = React.useState<ParallelModeConversion>('off');
  const [rebuiltPhraseCount, setRebuiltPhraseCount] = React.useState<number | null>(null);
  const [eventStream] = React.useState(
    () => new EventStreamPhaser2(createTimeKeeper(), new TimingOffsetsConfig(), null),
  );

  React.useLayoutEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    const observer = new ResizeObserver(updateDimensions);
    observer.observe(container);
    updateDimensions();
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const initialRequests: Array<{ sequenceId: number }> = [];
    let initialFillComplete = false;
    const handlePhraseRequest = ({ sequenceId }: { sequenceId: number }) => {
      if (initialFillComplete) {
        NotifyPhaserEvents.PhraseReady({ phrase: buildPhrase(sequenceId), sequenceId });
        return;
      }

      initialRequests.push({ sequenceId });
      if (initialRequests.length < 2) return;

      initialFillComplete = true;
      queueMicrotask(() => {
        initialRequests.forEach((request) => {
          NotifyPhaserEvents.PhraseReady({
            phrase: buildPhrase(request.sequenceId),
            sequenceId: request.sequenceId,
          });
        });
      });
    };

    PhaserToReactListeners.FetchPhrase(handlePhraseRequest, undefined, false, true);

    return () => RemoveListener('FetchPhrase', handlePhraseRequest);
  }, []);

  React.useEffect(() => {
    NotifyPhaserEvents.SetSightReadingTranspose({
      semitones: transposeSemitones,
      modeConversion,
    });
  }, [modeConversion, transposeSemitones]);

  React.useEffect(() => {
    PhaserToReactListeners.SightReadingTransposeApplied(({ rebuiltPhraseCount: count }) => {
      setRebuiltPhraseCount(count);
    });
    return () => RemoveListener('SightReadingTransposeApplied');
  }, []);

  return (
    <Box
      data-sight-reading-transposition-fixture
      data-rebuilt-phrase-count={rebuiltPhraseCount ?? ''}
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F4E5C7',
      }}
    >
      <Box
        sx={{
          minHeight: '92px',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 4,
          color: '#ECECED',
          backgroundColor: '#050B13',
        }}
      >
        <Box sx={{ fontWeight: 700 }}>Sight-reading transposition</Box>
        <TextField
          select
          size="small"
          value={`${transposeSemitones}`}
          onChange={(event) => setTransposeSemitones(Number(event.target.value))}
          inputProps={{ 'aria-label': 'Sight-reading transposition' }}
          data-sight-reading-transpose-select
          sx={{ width: 150, backgroundColor: '#FFFFFF', borderRadius: 1 }}
        >
          {TRANSPOSE_OPTIONS.map((value) => (
            <MenuItem key={value} value={`${value}`} data-transpose-option={value}>
              {value === 0 ? 'Concert (0)' : `${value > 0 ? '+' : ''}${value}`}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          value={modeConversion}
          onChange={(event) => setModeConversion(event.target.value as ParallelModeConversion)}
          inputProps={{ 'aria-label': 'Sight-reading scale or mode' }}
          data-sight-reading-mode-select
          sx={{ width: 180, backgroundColor: '#FFFFFF', borderRadius: 1 }}
        >
          {MODE_CONVERSION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ opacity: 0.72 }}>Real Phaser renderer, two-phrase full queue</Box>
        {rebuiltPhraseCount !== null && (
          <Box data-transposition-rebuilt-count sx={{ opacity: 0.72 }}>
            Rebuilt phrases: {rebuiltPhraseCount}
          </Box>
        )}
      </Box>
      <Box
        ref={(node: HTMLElement | null) => {
          parentRef.current = node ?? undefined;
        }}
        sx={{ minHeight: 0, flex: 1, position: 'relative' }}
      >
        {dimensions && (
          <Game
            eventStream={eventStream}
            width={dimensions.width}
            height={dimensions.height}
            togglePlayPause={() => {}}
            mobile={false}
            parentRef={parentRef}
            dispatch={dispatch}
            zoomLevel={0.9}
            goalTempo={72}
            ataEnabled={false}
            playing={false}
            freePlayMode={false}
            usingCustomGoalTempo={false}
            scoreTheme="light"
            transposeSemitones={transposeSemitones}
            modeConversion={modeConversion}
            cursorIsVisible={false}
          />
        )}
      </Box>
    </Box>
  );
};

export default SightReadingTranspositionVisualFixture;
